import { z } from "zod";
import {
  CHECKOUT_MODES,
  CHECKOUT_SORTS,
  MAX_PICKING_ORDERS,
  MAX_SCAN_CODE_LENGTH,
  MAX_STOCK_QTY_Q4,
  type CheckoutMode,
  type CheckoutProgress,
  type PickingLine,
} from "@cx/domain";
import { ulidParam } from "./ids";
import type { SalesOrderStatusRef } from "./sales-orders";
import type { DocumentTransitionResult } from "./statuses";

// ===== Checkout de pedidos (RF-VEN-07) =====

const nullish = <T extends z.ZodType>(s: T) => s.nullish().transform((v) => v ?? null);

/** Código bipado: sem caracteres de controle (Enter/Tab do leitor) e sem espaços nas pontas, 1..60 caracteres. */
const scanCode = z
  .string("Informe o código")
  .max(200, "Código longo demais")
  // eslint-disable-next-line no-control-regex
  .transform((v) => v.replace(/[\u0000-\u001f\u007f]/g, "").trim())
  .pipe(
    z.string().min(1, "Informe o código").max(MAX_SCAN_CODE_LENGTH, `Até ${MAX_SCAN_CODE_LENGTH} caracteres`),
  );
const sort = z.enum(CHECKOUT_SORTS, "Ordenação inválida").default("oldest");

/**
 * Abrir a conferência. Por pedido: o código bipado (número, nº na loja, pedido do cliente) ou o id escolhido na fila,
 * um dos dois. Por produto único: o código do produto; o pedido sai da fila (logística e ordenação).
 */
export const checkoutOpenInput = z
  .object({
    mode: z.enum(CHECKOUT_MODES, "Modo inválido").default("by_order"),
    code: nullish(scanCode),
    salesOrderId: nullish(ulidParam),
    carrierId: nullish(ulidParam),
    sort,
  })
  .superRefine((v, ctx) => {
    if (v.mode === "by_order" && v.code === null && v.salesOrderId === null)
      ctx.addIssue({ code: "custom", path: ["code"], message: "Bipe ou digite o pedido" });
    else if (v.mode === "single_product" && v.code === null)
      ctx.addIssue({ code: "custom", path: ["code"], message: "Bipe ou digite o produto" });
    else if (v.salesOrderId !== null && (v.code !== null || v.mode === "single_product"))
      ctx.addIssue({
        code: "custom",
        path: ["salesOrderId"],
        message: "Informe o código ou o pedido, não os dois",
      });
  });
export type CheckoutOpenInput = z.output<typeof checkoutOpenInput>;

export const checkoutReadInput = z.object({
  code: scanCode,
  qtyQ4: z
    .number("Informe a quantidade")
    .int("Quantidade inválida")
    .min(1, "A quantidade deve ser maior que zero")
    .max(MAX_STOCK_QTY_Q4, "Quantidade acima do limite")
    .default(10_000),
});
export type CheckoutReadInput = z.output<typeof checkoutReadInput>;

export const checkoutFinishInput = z.object({ partial: z.boolean("Informe sim ou não").default(false) });
export type CheckoutFinishInput = z.output<typeof checkoutFinishInput>;

export const checkoutSettingsInput = z.object({
  sourceStatusIds: z
    .array(ulidParam, "Escolha as situações")
    .min(1, "Escolha ao menos uma situação")
    .max(20, "No máximo 20 situações")
    .refine((l) => new Set(l).size === l.length, "Situação repetida"),
  doneStatusId: ulidParam,
  partialStatusId: nullish(ulidParam),
});
export type CheckoutSettingsInput = z.output<typeof checkoutSettingsInput>;

export const checkoutQueueQuery = z.object({
  statusId: ulidParam.optional(),
  carrierId: ulidParam.optional(),
  sort,
  cursor: z.string().max(200).optional(),
  limit: z.coerce.number().int().min(1).max(100).default(50),
});
export type CheckoutQueueQuery = z.output<typeof checkoutQueueQuery>;

/** `ids` separados por vírgula (pedidos selecionados); sem eles, os primeiros da fila filtrada. */
export const checkoutPickingQuery = z.object({
  ids: z
    .string()
    .max(MAX_PICKING_ORDERS * 27 + 100)
    .transform((v) => v.split(",").filter(Boolean))
    .pipe(
      z
        .array(ulidParam)
        .min(1, "Selecione os pedidos")
        .max(MAX_PICKING_ORDERS, `No máximo ${MAX_PICKING_ORDERS} pedidos`)
        .refine((l) => new Set(l).size === l.length, "Pedido repetido"),
    )
    .optional(),
  statusId: ulidParam.optional(),
  carrierId: ulidParam.optional(),
  sort,
});
export type CheckoutPickingQuery = z.output<typeof checkoutPickingQuery>;

// ----- visões -----

export type CheckoutSettings = {
  sourceStatusIds: string[];
  doneStatusId: string;
  partialStatusId: string | null;
};

export type CheckoutOptions = {
  settings: CheckoutSettings;
  /** Situações (ativas) do módulo de pedidos, na ordem do quadro. */
  statuses: SalesOrderStatusRef[];
  carriers: { id: string; name: string }[];
};

export type CheckoutSessionRef = {
  id: string;
  checkedQ4: number;
  requiredQ4: number;
  partial: boolean;
};

export type CheckoutQueueItem = {
  id: string;
  number: number;
  date: string;
  contactName: string;
  carrierName: string | null;
  itemsCount: number;
  status: SalesOrderStatusRef;
  session: CheckoutSessionRef | null;
};
export type CheckoutQueueResult = {
  data: CheckoutQueueItem[];
  nextCursor: string | null;
  /** Total da fila filtrada só na primeira página; com `cursor`, -1. */
  summary: { count: number };
};

export type CheckoutLineView = {
  id: string;
  /** Posição do item no pedido; o componente de kit repete a do kit. */
  itemPosition: number;
  kitName: string | null;
  productId: string;
  name: string;
  sku: string | null;
  gtin: string | null;
  gtinTax: string | null;
  unit: string;
  location: string | null;
  warehouseName: string;
  requiredQ4: number;
  checkedQ4: number;
  /** Físico no depósito da linha; ausente sem permissão de ver estoque. */
  stockQ4?: number | null;
};

export type CheckoutReadView = {
  id: string;
  kind: "read" | "reset";
  code: string | null;
  qtyQ4: number;
  productName: string | null;
  userId: string | null;
  createdAt: string;
};

export type CheckoutSessionState = "open" | "finished" | "canceled";

export type CheckoutSessionView = {
  id: string;
  mode: CheckoutMode;
  state: CheckoutSessionState;
  order: {
    id: string;
    number: number;
    date: string;
    contactName: string;
    carrierName: string | null;
    status: SalesOrderStatusRef;
  };
  lines: CheckoutLineView[];
  progress: CheckoutProgress;
  /** Últimas leituras, da mais nova para a mais antiga. */
  reads: CheckoutReadView[];
  stockVisible: boolean;
  startedAt: string;
  partialAt: string | null;
  finishedAt: string | null;
  outcome: "complete" | "partial" | null;
};

export type CheckoutFinishResult = {
  sessionId: string;
  outcome: "complete" | "partial";
  state: CheckoutSessionState;
  statusChange: DocumentTransitionResult;
  /** Etiqueta de transporte: só com a integração logística (T3.4). */
  labelAvailable: boolean;
};

export type CheckoutOpenResult = {
  sessionId: string;
  salesOrderId: string;
  /** Já havia sessão aberta para o pedido (outro operador): entrou nela. */
  joined: boolean;
  /** Por produto único: a leitura já registrada e, se completou, a conclusão. */
  read: CheckoutReadResult | null;
  finish: CheckoutFinishResult | null;
};

export type CheckoutReadResult = {
  sessionId: string;
  productName: string;
  lineIds: string[];
  progress: CheckoutProgress;
};

export type CheckoutPickingList = {
  orders: { id: string; number: number }[];
  lines: PickingLine[];
  /** A fila filtrada tinha mais pedidos que o teto do relatório. */
  truncated: boolean;
  /** Números dos pedidos deixados de fora por passarem do teto de itens a conferir (kits expandidos). */
  skipped: number[];
};

/** Mensagem do WebSocket do checkout: só ids e estado; a tela relê pela API (que aplica RBAC e carteira). */
export type CheckoutSocketEvent = {
  type: "checkout.changed";
  sessionId: string;
  salesOrderId: string;
  state: CheckoutSessionState;
};
